import { useLazyQuery } from '@apollo/client';
import { Select } from 'antd';
import { debounce, get, map, trim, uniqBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { LIMIT } from '../common/constants';

const { Option } = Select;
let searchDebounce = null;

const CommonSelect = (props) => {
  const {
    placeholder = 'Search',
    query,
    fetchPolicy,
    variables = {},
    responsePath,
    valuePath,
    labelPath,
    getLabel,
    showSearch,
    conditionToCheckBeforeQuery = true,
    isDataDependent = [],
    customOptions = [],
    useEffectDeps = [],
    inputRef = null,
    getApiData,
    uniqByVariable = 'id',
    resolveMissingSelected = false,
    value,
    ...rest
  } = props;
  const [listData, setListData] = useState([...customOptions] || []);
  const [hasMore, setHasMore] = useState(true);
  const [skipData, setSkipData] = useState(0);
  const [searchText, setSearchText] = useState('');
  // Tracks whether the first page for the *current* deps/search has actually
  // resolved. Without this, resolveMissingSelected's effect can fire on the
  // very first render (before the mount fetch below resolves) — racing the
  // two fetches, scrambling skip order, and breaking pagination for a
  // selected value that lives several pages in.
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

  const [fetchListData, { loading }] = useLazyQuery(query, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const data = get(res, responsePath, []);
      const count = data?.length;
      const records = [...listData, ...data];
      setListData(uniqBy([...customOptions, ...records], uniqByVariable));
      setHasMore(count >= LIMIT);
      setHasFetchedOnce(true);
      if (getApiData) {
        getApiData(records);
      }
    },
    onError() {},
  });

  useEffect(() => {
    if (isDataDependent) {
      setListData([]);
      setSkipData(0);
    }
  }, isDataDependent);

  // Fires on mount and whenever the caller's search/filter deps change.
  // Resets any previously-loaded pages first — otherwise a new search's
  // results get appended to the stale list instead of replacing it, and the
  // next scroll-to-load-more request reuses the stale skip offset.
  useEffect(() => {
    setListData([...customOptions]);
    setSkipData(0);
    setHasMore(true);
    setHasFetchedOnce(false);
    if (conditionToCheckBeforeQuery) {
      fetchListData({
        fetchPolicy,
        variables: {
          ...variables,
          filters: {
            ...(variables?.filters || {}),
            skip: 0,
            limit: LIMIT,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, useEffectDeps);

  // A pre-selected value (e.g. restored from a URL query param on refresh)
  // may live beyond the first page fetched above — without this, the Select
  // can't resolve a label for it and falls back to showing the raw id. Opt-in
  // only, since most CommonSelect usages don't persist ids across a refresh.
  // Skipped while an active search filter is applied: in that case the
  // selected value legitimately not appearing in the (correctly filtered)
  // results is expected, not something to "fix" by auto-paging through the
  // filtered set looking for an unrelated id — doing so fights the search.
  const hasActiveSearchFilter = Boolean(
    variables?.searchString ||
      variables?.filters?.searchStrings?.length,
  );

  useEffect(() => {
    if (
      !resolveMissingSelected ||
      !hasMore ||
      loading ||
      !hasFetchedOnce ||
      hasActiveSearchFilter
    ) {
      return;
    }
    const selectedValues = Array.isArray(value)
      ? value
      : [value].filter(Boolean);
    if (selectedValues?.length === 0) return;
    const loadedValues = new Set(listData?.map((item) => get(item, valuePath)));
    const isFullyLoaded = selectedValues?.every((item) =>
      loadedValues?.has(item),
    );
    if (isFullyLoaded) return;
    const newSkip = skipData + LIMIT;
    fetchListData({
      variables: {
        ...variables,
        filters: { ...(variables?.filters || {}), skip: newSkip, limit: LIMIT },
      },
    });
    setSkipData(newSkip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    listData,
    value,
    hasMore,
    loading,
    resolveMissingSelected,
    hasFetchedOnce,
    hasActiveSearchFilter,
  ]);

  const searchQuery = (search) => {
    setSearchText(trim(search));
    fetchListData({
      fetchPolicy: 'network-only',
      variables: {
        ...variables,
        searchString: trim(search),
        filters: {
          ...(variables?.filters || {}),
          skip: skipData,
          limit: 10,
        },
      },
    });
  };

  const handleSearch = (searchValue) => {
    setSkipData(0);
    if (searchDebounce) {
      searchDebounce.cancel();
      searchDebounce = null;
    }
    searchDebounce = debounce(searchQuery, 500);
    searchDebounce(trim(searchValue));
  };

  const handleClear = () => {
    if (searchText) {
      searchQuery();
    }
  };
  // While resolveMissingSelected is still paging through results looking for
  // the selected id's option, that option isn't loaded yet, so antd would
  // otherwise render the raw id as the visible text. Hide the value until
  // it resolves (or gives up) instead of flashing the raw id.
  const selectedValues = Array.isArray(value)
    ? value
    : [value].filter(Boolean);
  const loadedValues = new Set(listData?.map((item) => get(item, valuePath)));
  const hasUnresolvedSelection =
    resolveMissingSelected &&
    selectedValues?.length > 0 &&
    selectedValues?.some((item) => !loadedValues?.has(item)) &&
    hasMore;

  const handleScroll = async (event) => {
    const { target } = event;
    const { scrollTop, clientHeight, scrollHeight } = target || {};
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 5;
    if (scrolledToBottom && hasMore) {
      const newSkip = skipData + LIMIT;
      await fetchListData({
        variables: {
          ...variables,
          filters: {
            ...variables?.filters,
            skip: newSkip,
            limit: LIMIT,
          },
        },
      });
      setSkipData(newSkip);
    }
  };
  return (
    <>
      <Select
        ref={inputRef}
        value={hasUnresolvedSelection ? undefined : value}
        placeholder={placeholder}
        loading={loading || hasUnresolvedSelection}
        showSearch={showSearch}
        onSearch={showSearch && handleSearch}
        onClear={handleClear}
        onBlur={handleClear}
        onPopupScroll={handleScroll}
        filterOption={(inputValue, option) =>
          option?.label?.toLowerCase()?.includes(inputValue?.toLowerCase())
        }
        {...rest}
      >
        <>
          {map(uniqBy([...listData], valuePath), (item) => {
            const optionValue = get(item, valuePath);
            const optionLabel = getLabel ? getLabel(item) : get(item, labelPath);
            return (
              <Option
                key={`option-key:${optionValue}`}
                value={optionValue}
                label={optionLabel}
              >
                {optionLabel}
              </Option>
            );
          })}
        </>
      </Select>
    </>
  );
};

export default CommonSelect;
